import {LitElement} from 'lit';

import {isDev} from '#utils/general.js';

import {getParams, getRule} from '../utils/routes.js';
import type {FlywheelRouter} from './router.js';

// A Vortex component; extends LitElement and HTMLElement
export class FlywheelComponent extends LitElement {
  static tagRegistry = new Set<string>();
  static paths: string[] = [];
  static router: FlywheelRouter;
  static instances: Map<string, FlywheelComponent[]> = new Map();

  protected path: string;
  private disconnectController = new AbortController();

  lifecycleComplete = false;
  queries: URLSearchParams;

  constructor() {
    super();

    this.path = window.location.pathname || '';
    this.queries = new URLSearchParams(window.location.search);

    if (this.tagName) {
      FlywheelComponent.tagRegistry.add(this.tagName);

      if (isDev) {
        if (!FlywheelComponent.instances.has(this.tagName)) {
          FlywheelComponent.instances.set(this.tagName, []);
        }
        FlywheelComponent.instances.get(this.tagName)?.push(this);
      }
    }
  }

  private finishLifecylce() {
    if (!this.lifecycleComplete) {
      this.rendered();
      this.lifecycleComplete = true;
      this.dispatchEvent(new CustomEvent('rendered'));
    }
  }

  /**
   * Abort controller signal
   * Useful for automatically remove listeners when the component is removed from the DOM
   */
  get disconnectSignal() {
    return this.disconnectController.signal;
  }

  beforeLoad(): void {}
  load(): void {}
  rendered(): void {}

  // Returns valid route params if there's a
  // matching path
  get params(): {[k: string]: string | number} {
    const pathname = this.path;
    const rule = getRule(pathname, FlywheelComponent.paths);
    return getParams(pathname, rule) || {};
  }

  get router(): FlywheelRouter {
    return FlywheelComponent.router;
  }

  connectedCallback() {
    super.connectedCallback();
    this.disconnectController = new AbortController();

    const context = (this.shadowRoot?.getRootNode() ||
      this.getRootNode()) as Node;

    if (context) {
      // Mutation observer for after render/load hook
      const observer = new MutationObserver(() => {
        this.finishLifecylce();
        observer.disconnect();
      });
      observer.observe(context, {
        attributes: false,
        childList: true,
        subtree: true,
      });
    }

    // Before load hook
    this.beforeLoad = this.beforeLoad.bind(this);
    this.beforeLoad();

    this.render();

    // On load hook
    this.load = this.load.bind(this);
    this.load();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.disconnectController.abort();
    this.dispatchEvent(new CustomEvent('disconnect'));

    if (isDev) {
      const instances = FlywheelComponent.instances.get(this.tagName);

      if (instances) {
        const updatedInstances = instances.filter(inst => inst !== this);

        if (updatedInstances.length === 0) {
          FlywheelComponent.instances.delete(this.tagName);
        } else {
          FlywheelComponent.instances.set(this.tagName, updatedInstances);
        }
      }
    }
  }

  firstUpdated() {
    this.finishLifecylce();
  }
}
